/**
 * Product Card Variant Selector
 * Handles variant selection and add-to-cart on product cards.
 * Cart updates are delegated to the theme.
 */

if (!customElements.get('product-variants-card')) {
  customElements.define('product-variants-card', class ProductVariantsCard extends HTMLElement {
    constructor() {
      super();

      this.form = this.querySelector('.card-variant-form');
      this.variantIdInput = this.querySelector('input[name="id"]');
      this.selects = this.querySelectorAll('.card-variant-select');
      this.addBtn = this.querySelector('[data-add-to-cart]');
      this.btnText = this.querySelector('.card-variant-btn-text');
      this.spinner = this.querySelector('.card-variant-spinner');
      this.priceEl = this.querySelector('[data-variant-price]');
      this.compareEl = this.querySelector('[data-variant-compare-price]');
      this.btnPriceWrapper = this.querySelector('[data-btn-price-wrapper]');

      const json = this.querySelector('[data-variant-json]');
      this.variants = json ? JSON.parse(json.textContent) : [];
      this.currentVariant = this.variants.find(v => v.id === parseInt(this.variantIdInput?.value)) || this.variants[0];
      this.isSubmitting = false;

      this.bindEvents();

      // Set initial availability
      if (this.selects.length > 0) {
        this.updateAvailability();
      }
    }

    bindEvents() {
      this.selects.forEach(s => s.addEventListener('change', this.onVariantChange.bind(this)));
      if (this.form) {
        this.form.addEventListener('submit', this.onSubmit.bind(this));
      }
      // Third-party apps (BOGOS Free Gifts, etc.) intercept the form submit
      // and call preventDefault, so onSubmit never runs and our spinner never shows.
      // Bind a click listener directly on the button to guarantee visual feedback,
      // without preventing the third-party app from doing its work.
      if (this.addBtn) {
        this.addBtn.addEventListener('click', this.onButtonClick.bind(this));
      }
    }

    /* ── Click feedback (BOGOS / SlideCart safe) ──── */

    onButtonClick() {
      if (!this.currentVariant?.available) return;
      if (this.addBtn?.classList.contains('loading')) return;

      this.setLoading(true);
      const t0 = performance.now();

      const stop = () => {
        if (this._cvsObserver) { this._cvsObserver.disconnect(); this._cvsObserver = null; }
        if (this._cvsTimer)    { clearTimeout(this._cvsTimer);    this._cvsTimer = null; }
        const elapsed = performance.now() - t0;
        const remaining = Math.max(0, 400 - elapsed);
        setTimeout(() => this.setLoading(false), remaining);
      };

      // Watch the cart icon bubble — count updates when add succeeds.
      const bubble = document.getElementById('cart-icon-bubble');
      if (bubble) {
        this._cvsObserver = new MutationObserver(stop);
        this._cvsObserver.observe(bubble, { childList: true, subtree: true, characterData: true });
      }

      // Hard fallback in case nothing observable happens.
      this._cvsTimer = setTimeout(stop, 3000);
    }

    /* ── Variant change ─────────────────────────── */

    onVariantChange() {
      this.matchVariant();
      this.syncHiddenInput();
      this.renderPrice();
      this.renderButton();
      this.updateAvailability();
      this.updateProductLink();
    }

    matchVariant() {
      const selected = this.selectedOptions();
      this.currentVariant = this.variants.find(v =>
        selected.every((opt, i) => v[`option${i + 1}`] === opt)
      ) || null;
    }

    selectedOptions() {
      return Array.from(this.selects).map(s => s.value);
    }

    syncHiddenInput() {
      if (!this.currentVariant || !this.variantIdInput) return;
      this.variantIdInput.value = this.currentVariant.id;
    }

    /* ── Price ───────────────────────────────────── */

    renderPrice() {
      if (!this.currentVariant) return;

      if (this.priceEl) {
        this.priceEl.textContent = this.formatMoney(this.currentVariant.price);
      }

      if (this.compareEl) {
        const hasCompare = this.currentVariant.compare_at_price && this.currentVariant.compare_at_price > this.currentVariant.price;
        if (hasCompare) {
          this.compareEl.textContent = this.formatMoney(this.currentVariant.compare_at_price);
          this.compareEl.removeAttribute('hidden');
        } else {
          this.compareEl.setAttribute('hidden', '');
        }
      }
    }

    /* ── Button state ────────────────────────────── */

    renderButton() {
      if (!this.addBtn || !this.btnText) return;
      const ok = this.currentVariant?.available;
      this.addBtn.disabled = !ok;
      this.btnText.textContent = ok
        ? (window.variantStrings?.addToCart || 'Ajouter')
        : (window.variantStrings?.soldOut || 'Épuisé');

      // Show/hide price inside button based on availability
      if (this.btnPriceWrapper) {
        this.btnPriceWrapper.hidden = !ok;
      }
    }

    /* ── Availability per option ─────────────────── */

    updateAvailability() {
      const selected = this.selectedOptions();
      const soldOutLabel = window.variantStrings?.soldOut || 'Sold out';

      this.selects.forEach((select, si) => {
        select.querySelectorAll('option').forEach(opt => {
          if (opt.value === '') return;

          const available = this.variants.some(v => {
            if (v[`option${si + 1}`] !== opt.value) return false;
            if (!v.available) return false;

            // Single option product — just check availability
            if (this.selects.length === 1) return true;

            // Multi-option: check against currently selected OTHER options
            return selected.every((sel, idx) =>
              idx === si || v[`option${idx + 1}`] === sel
            );
          });

          opt.disabled = !available;

          // Build label: value + per-gram price (if applicable) + sold-out suffix
          let label = opt.value;
          const ppg = this.calcPpg(opt.value, si);
          if (ppg) label += ` (${ppg})`;
          if (!available) label += ` - ${soldOutLabel}`;
          opt.textContent = label;
        });
      });
    }

    /* ── Per-gram price for an option value ─────── */

    calcPpg(optValue, optionIndex) {
      // Extract leading number before 'g' (e.g. "10g" → 10, "2.5g" → 2.5)
      const match = optValue.match(/^(\d+[.,]?\d*)\s*g/i);
      if (!match) return null;

      const grams = parseFloat(match[1].replace(',', '.'));
      if (!grams || grams <= 0) return null;

      // Find the cheapest variant matching this option value
      const optKey = `option${optionIndex + 1}`;
      let cheapest = null;
      for (const v of this.variants) {
        if (v[optKey] !== optValue || v.price <= 0) continue;
        if (!cheapest || v.price < cheapest.price) cheapest = v;
      }
      if (!cheapest) return null;

      const ppg = cheapest.price / 100 / grams;
      // Format: "6,00€/g" or "8,33€/g"
      const formatted = ppg % 1 === 0
        ? ppg.toFixed(0) + '€/g'
        : ppg.toFixed(2).replace('.', ',') + '€/g';
      return formatted;
    }

    /* ── Product link update ──────────────────────── */

    updateProductLink() {
      if (!this.currentVariant || !this.dataset.url) return;
      const card = this.closest('.product-card');
      const link = card?.querySelector('[data-product-card-title-id]');
      if (link) {
        const url = new URL(this.dataset.url, window.location.origin);
        url.searchParams.set('variant', this.currentVariant.id);
        link.href = url.toString();
      }
    }

    /* ── Add to cart ──────────────────────────────── */

    async onSubmit(e) {
      e.preventDefault();
      e.stopPropagation();
      if (this.isSubmitting || !this.currentVariant?.available) return;
      this.isSubmitting = true;
      this.setLoading(true);

      this.syncHiddenInput();

      // Force a paint cycle so the spinner is visible before fetch starts.
      // Without this, fast responses resolve before the browser repaints.
      await new Promise(r => requestAnimationFrame(() => requestAnimationFrame(r)));

      const t0 = performance.now();

      try {
        // Build the request exactly like the theme's product-form.js:
        // Use FormData so Shopify returns pre-rendered section HTML
        const drawer = document.querySelector('cart-drawer');
        const sections = drawer && typeof drawer.getSectionsToRender === 'function'
          ? drawer.getSectionsToRender().map(s => s.id)
          : ['cart-drawer', 'cart-icon-bubble'];

        const formData = new FormData();
        formData.append('id', this.currentVariant.id);
        formData.append('quantity', 1);
        formData.append('sections', sections.join(','));
        formData.append('sections_url', window.location.pathname);

        const res = await fetch(window.routes.cart_add_url, {
          method: 'POST',
          headers: { 'Accept': 'application/javascript' },
          body: formData
        });
        const data = await res.json();

        if (res.ok) {
          this.onAddSuccess(data);
        } else {
          this.onAddError(data);
        }
      } catch (err) {
        this.onAddError({ message: err.message });
      } finally {
        // Guarantee a perceptible spin even if SlideCart/network responds instantly.
        const elapsed = performance.now() - t0;
        if (elapsed < 400) await new Promise(r => setTimeout(r, 400 - elapsed));
        this.setLoading(false);
        this.isSubmitting = false;
      }
    }

    onAddSuccess(data) {
      const drawer = document.querySelector('cart-drawer');
      if (!drawer) return;

      // FIX: Remove is-empty class from cart-drawer and cart-drawer-items.
      // Without this, CSS rules like ".is-empty .cart__contents { display: none }"
      // and "cart-drawer.is-empty .drawer__header { display: none }" hide the
      // newly added cart items — making the drawer appear empty even though the
      // product rows are in the DOM. This mirrors what product-form.js does in
      // its finally{} block.
      drawer.classList.remove('is-empty');
      const drawerItems = drawer.querySelector('cart-drawer-items');
      if (drawerItems) drawerItems.classList.remove('is-empty');

      if (data.sections) {
        const wasDeferred = !drawer.querySelector('.drawer__inner');

        if (wasDeferred) {
          // Empty-cart / deferred state: .drawer__inner doesn't exist in the DOM yet
          // (content is trapped inside a <template class="deferred"> tag).
          // Bootstrap the full section HTML into <cart-drawer> — this creates
          // #CartDrawer, .drawer__inner, and all cart item rows in one shot.
          // Do NOT call renderContents afterwards — it would overwrite #CartDrawer
          // innerHTML a second time, causing a race condition where product rows
          // intermittently disappear while the progress bar / totals remain visible.
          this.manualSectionUpdate(data.sections);
          this.openDrawerSafe(drawer);
        } else {
          // Normal state: .drawer__inner already exists in the DOM.
          // Delegate to the theme's renderContents — it handles innerHTML swap,
          // drawer.open(), focus traps, and overlay state cleanly.
          if (typeof drawer.renderContents === 'function') {
            try {
              drawer.renderContents(data);
            } catch (_) {
              this.manualSectionUpdate(data.sections);
              this.openDrawerSafe(drawer);
            }
          } else {
            this.manualSectionUpdate(data.sections);
            this.openDrawerSafe(drawer);
          }
        }
      } else {
        this.openDrawerSafe(drawer);
      }

      // After manual section update, the new cart-drawer-items element won't
      // have is-empty (Shopify rendered it with items), but re-check in case
      // the innerHTML swap created a fresh element.
      const freshDrawerItems = drawer.querySelector('cart-drawer-items');
      if (freshDrawerItems) freshDrawerItems.classList.remove('is-empty');

      // Notify theme subscribers (same event product-form.js fires)
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'product-form',
          productVariantId: this.currentVariant.id,
          cartData: data
        });
      }
    }

    /* ── Open drawer safely ───────────────────────── */

    openDrawerSafe(drawer) {
      setTimeout(() => {
        // Ensure the drawer is initialised (loads deferred CSS into <head>,
        // binds overlay click, etc.) — needed when the deferred <template>
        // was replaced by manualSectionUpdate instead of the normal theme flow.
        if (typeof drawer.init === 'function') drawer.init();

        if (!drawer.classList.contains('active') && typeof drawer.open === 'function') {
          drawer.open();
        }
      }, 100);
    }

    /* ── Manual section update (fallback for empty-cart state) ── */

    manualSectionUpdate(sections) {
      // Strip <template> tags before parsing — matches the theme's getSectionInnerHTML.
      // Without this, content inside <template> goes into a DocumentFragment that
      // querySelector cannot reach, so #CartDrawer / #cart-icon-bubble return null.
      const strip = (html) => html.replace(/<template\b[^>]*>|<\/template>/g, '');

      // Update cart drawer contents
      if (sections['cart-drawer']) {
        const fresh = new DOMParser().parseFromString(strip(sections['cart-drawer']), 'text/html');
        const newDrawer = fresh.querySelector('#CartDrawer');
        const oldDrawer = document.getElementById('CartDrawer');

        if (newDrawer && oldDrawer) {
          // Normal case: #CartDrawer exists in live DOM — update its innerHTML
          oldDrawer.innerHTML = newDrawer.innerHTML;
        } else if (newDrawer && !oldDrawer) {
          // Empty-cart / deferred state: #CartDrawer doesn't exist in DOM yet.
          // The entire content is inside a <template class="deferred"> tag.
          // Replace cart-drawer's innerHTML with the full section content
          // (CSS links + JS scripts + #CartDrawer div).
          const drawer = document.querySelector('cart-drawer');
          const newCartDrawerEl = fresh.querySelector('cart-drawer');
          if (drawer && newCartDrawerEl) {
            drawer.innerHTML = newCartDrawerEl.innerHTML;
          }
        }

        // FIX: Scripts set via innerHTML never execute. Re-create them so the
        // browser loads cart.js + quantity-popover.js — these define the custom
        // elements (CartRemoveButton, CartItems, CartDrawerItems) that make
        // delete buttons, quantity inputs, and cart updates interactive.
        const drawer = document.querySelector('cart-drawer');
        if (drawer) {
          drawer.querySelectorAll('script[src]').forEach(old => {
            // Respect the theme's loadedScripts de-dupe list
            if (window.loadedScripts && window.loadedScripts.includes(old.src)) {
              old.remove();
              return;
            }
            if (!window.loadedScripts) window.loadedScripts = [];
            window.loadedScripts.push(old.src);

            const live = document.createElement('script');
            live.src = old.src;
            document.body.appendChild(live);
            old.remove();
          });
        }
      }

      // Update cart icon bubble (count badge)
      // FIX: The section HTML wraps content in .shopify-section — there is no
      // #cart-icon-bubble inside it. That ID is on the page element that CONTAINS
      // the section output. So we must extract .shopify-section innerHTML (the
      // icon + count badge markup) and inject it into the existing page element.
      if (sections['cart-icon-bubble']) {
        const fresh = new DOMParser().parseFromString(strip(sections['cart-icon-bubble']), 'text/html');
        const newContent = fresh.querySelector('.shopify-section');
        const oldBubble = document.getElementById('cart-icon-bubble');
        if (newContent && oldBubble) {
          oldBubble.innerHTML = newContent.innerHTML;
        }
      }
    }

    onAddError(data) {
      this.toast(data.message || data.description || 'Impossible d\'ajouter au panier', true);
    }

    /* ── Loading state ────────────────────────────── */

    setLoading(on) {
      this.addBtn?.classList.toggle('loading', on);
      this.spinner?.classList.toggle('hidden', !on);
      this.btnText?.classList.toggle('hidden', on);
      // Use visibility (not display:none) so the price keeps its space
      // and the button height stays locked during loading
    }

    /* ── Toast notification ───────────────────────── */

    toast(msg, isError = false) {
      // Remove existing toast if any
      document.querySelector('.cvs-toast')?.remove();

      const el = document.createElement('div');
      el.className = 'cvs-toast';
      el.textContent = msg;
      el.style.cssText = `
        position:fixed; bottom:24px; right:24px;
        background:${isError ? '#d32f2f' : '#2e7d32'};
        color:#fff; padding:12px 22px; border-radius:8px;
        font-size:14px; font-weight:600; z-index:99999;
        box-shadow:0 4px 14px rgba(0,0,0,.18);
        animation:cvs-in .25s ease-out;
        pointer-events:none;
      `;
      document.body.appendChild(el);

      setTimeout(() => {
        el.style.animation = 'cvs-out .25s ease-in forwards';
        el.addEventListener('animationend', () => el.remove());
      }, 2200);
    }

    /* ── Money formatting ─────────────────────────── */

    formatMoney(cents) {
      if (typeof cents === 'string') cents = parseInt(cents, 10);
      const val = cents / 100;

      // Thousands + decimals
      const parts = val.toFixed(2).split('.');
      const whole = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.');  // 1.050
      const dec = parts[1];                                            // 00
      const commaFormat = `${whole},${dec}`;                           // 1.050,00
      const dotFormat = val.toFixed(2);                                // 1050.00
      const noDecimals = Math.floor(val).toString();
      const noDecimalsComma = whole;

      const fmt = window.theme?.settings?.moneyWithCurrencyFormat
        || '€{{amount_with_comma_separator}} EUR';

      return fmt
        .replace('{{amount_with_comma_separator}}', commaFormat)
        .replace('{{amount_no_decimals_with_comma_separator}}', noDecimalsComma)
        .replace('{{amount_with_apostrophe_separator}}', dotFormat.replace(/\B(?=(\d{3})+(?!\d))/g, "'"))
        .replace('{{amount_no_decimals}}', noDecimals)
        .replace('{{amount}}', dotFormat);
    }
  });
}

/* ── Toast keyframes (injected once) ────────────── */
if (!document.getElementById('cvs-keyframes')) {
  const s = document.createElement('style');
  s.id = 'cvs-keyframes';
  s.textContent = `
    @keyframes cvs-in  { from{transform:translateY(20px);opacity:0} to{transform:translateY(0);opacity:1} }
    @keyframes cvs-out { from{transform:translateY(0);opacity:1}   to{transform:translateY(20px);opacity:0} }
  `;
  document.head.appendChild(s);
}